import { useState } from 'react';
import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { Alert } from 'foundations-components/transitional/components/Alert';
import { Button } from 'foundations-components/transitional/components/Button';
import { LoadingButton } from 'foundations-components/transitional/components/LoadingButton';
import { TextArea } from 'foundations-components/transitional/components/TextArea';
import { Select } from 'foundations-components/components/Select';
import { Text } from 'foundations-components/components/Text';
import { DialogBody } from 'foundations-components/transitional/components/DialogBody';
import { DialogCloseButton } from 'foundations-components/transitional/components/DialogCloseButton';
import { DialogFooter } from 'foundations-components/transitional/components/DialogFooter';
import { DialogHeader } from 'foundations-components/transitional/components/DialogHeader';
import { FormControl } from 'foundations-components/transitional/components/FormControl';
import { LeftIcon } from 'foundations-assets/icons/components/LeftIcon';
import { submitAdditionalPqlForm } from 'ui-addon-upgrades/_core/pql/submitPql';
import { getRepName, getUpgradeDataTrackingProperties } from 'ui-addon-upgrades/ums/utils';
import { StyledModal } from '../common/components/StyledModal';
import { track } from '../common/eventTracking/tracker';
import { Flex } from 'foundations-components/components/Flex';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const PQL_SUBMISSION_STATUSES = {
  INITIAL: 'initial',
  LOADING: 'loading',
  FAILED: 'failed',
  SUCCEEDED: 'succeeded'
};
const MODAL_CLOSE_DELAY_MILLIS = 800;
const EmailModal = ({
  onClose,
  hideBackground,
  upgradeData,
  repInfo,
  onBackClick
}) => {
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [hasClickedSubmit, setHasClickedSubmit] = useState(false);
  const [pqlSubmissionStatus, setPqlSubmissionStatus] = useState(PQL_SUBMISSION_STATUSES.INITIAL);
  const handleSubmit = () => {
    setHasClickedSubmit(true);
    if (subject.length && body.length) {
      setPqlSubmissionStatus(PQL_SUBMISSION_STATUSES.LOADING);
      submitAdditionalPqlForm(Object.assign({}, upgradeData, {
        isAssignable: true,
        isRetail: false,
        repInfo
      }), {
        emailSubject: subject,
        emailBody: body
      }).then(() => {
        setPqlSubmissionStatus(PQL_SUBMISSION_STATUSES.SUCCEEDED);
        track('communicationMethodsInteraction', Object.assign({
          action: 'submitted email message',
          repName: getRepName(repInfo)
        }, getUpgradeDataTrackingProperties(upgradeData)));
        setTimeout(() => {
          onClose();
        }, MODAL_CLOSE_DELAY_MILLIS);
      }).catch(() => {
        //Do we want to set an alert in this case?
        setPqlSubmissionStatus(PQL_SUBMISSION_STATUSES.FAILED);
      });
    }
  };
  return /*#__PURE__*/_jsxs(StyledModal, {
    "data-test-id": "email-modal",
    width: 600,
    hideBackground: hideBackground,
    use: "conversational",
    children: [/*#__PURE__*/_jsxs(DialogHeader, {
      children: [/*#__PURE__*/_jsx(DialogCloseButton, {
        onClick: () => onClose()
      }), /*#__PURE__*/_jsx(Text, {
        as: "h2",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "upgrades.emailModal.header"
        })
      })]
    }), /*#__PURE__*/_jsxs(DialogBody, {
      children: [onBackClick && /*#__PURE__*/_jsxs(Flex, {
        alignItems: "center",
        marginBlockStart: "400",
        marginBlockEnd: "400",
        children: [/*#__PURE__*/_jsx(LeftIcon, {}), /*#__PURE__*/_jsx(Button, {
          use: "link",
          onClick: () => {
            track('communicationMethodsInteraction', {
              action: 'back to other options from email modal'
            });
            onBackClick();
          },
          paddingInlineStart: "200",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "upgrades.communicationMethods.backButton"
          })
        })]
      }), /*#__PURE__*/_jsx(FormControl, {
        label: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "upgrades.emailModal.subject"
        }),
        error: hasClickedSubmit && !subject.length,
        validationMessage: hasClickedSubmit && !subject.length && /*#__PURE__*/_jsx(FormattedMessage, {
          message: "upgrades.emailModal.errorMessages.subject"
        }),
        children: /*#__PURE__*/_jsx(Select, {
          selectedValues: subject ? [subject] : [],
          "data-test-id": "pql-email-subject-dropdown",
          searchable: false,
          onSelectedOptionChange: selectedOption => {
            var _selectedOption$value;
            return setSubject((selectedOption === null || selectedOption === void 0 || (_selectedOption$value = selectedOption.value) === null || _selectedOption$value === void 0 ? void 0 : _selectedOption$value.toString()) || '');
          },
          selectedItemsPlaceholder: I18n.text('upgrades.emailModal.subjectDropdown.placeholder')
          //The message subjects are submitted to 53 in English
          ,
          options: [{
            text: I18n.text(`upgrades.emailModal.subjectDropdown.options.purchase`),
            value: `I'm interested in purchasing a product`
          }, {
            text: I18n.text(`upgrades.emailModal.subjectDropdown.options.learnMore`),
            value: 'I want to learn more about a product or feature'
          }, {
            text: I18n.text(`upgrades.emailModal.subjectDropdown.options.help`),
            value: 'I need setup help or have a technical question'
          }, {
            text: I18n.text(`upgrades.emailModal.subjectDropdown.options.otherOption`),
            value: 'Other'
          }]
        })
      }), /*#__PURE__*/_jsx(FormControl, {
        label: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "upgrades.emailModal.body"
        }),
        error: hasClickedSubmit && body === '',
        validationMessage: hasClickedSubmit && body === '' && /*#__PURE__*/_jsx(FormattedMessage, {
          message: "upgrades.emailModal.errorMessages.body"
        }),
        children: /*#__PURE__*/_jsx(TextArea, {
          "data-test-id": "pql-email-message-textarea",
          placeholder: I18n.text('upgrades.emailModal.bodyPlaceholder'),
          rows: 10,
          resize: false,
          value: body,
          onChange: ({
            target: {
              value
            }
          }) => setBody(value)
        })
      }), pqlSubmissionStatus === PQL_SUBMISSION_STATUSES.FAILED && /*#__PURE__*/_jsx(Alert, {
        titleText: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "upgrades.emailModal.submissionErrors.title"
        }),
        type: "danger",
        marginBlockStart: "500",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "upgrades.emailModal.submissionErrors.body"
        })
      })]
    }), /*#__PURE__*/_jsxs(DialogFooter, {
      children: [/*#__PURE__*/_jsx(LoadingButton, {
        use: "primary",
        "data-test-id": "pql-email-submit",
        onClick: handleSubmit,
        loading: pqlSubmissionStatus === PQL_SUBMISSION_STATUSES.LOADING,
        keepResultAfterFinish: pqlSubmissionStatus !== PQL_SUBMISSION_STATUSES.FAILED,
        failed: pqlSubmissionStatus === PQL_SUBMISSION_STATUSES.FAILED,
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "upgrades.emailModal.send"
        })
      }), /*#__PURE__*/_jsx(Button, {
        use: "secondary",
        onClick: () => onClose(),
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "upgrades.emailModal.cancel"
        })
      })]
    })]
  });
};
export default EmailModal;